import { useState, useEffect, useRef, useCallback } from 'react';
import './AnnouncementBar.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/* ─────────────────────────────────────────────
   Fallback when API is empty or fails — edit here only as last resort
   Change VERSION any time content changes so
   users who dismissed the old bar see the new one
───────────────────────────────────────────── */
const VERSION = 'v5';
const STORAGE_KEY = `announcement_closed_${VERSION}`;

const FALLBACK_MESSAGES = [
  { _id: 'f1', text: 'Complimentary shipping on orders above ', highlight: '₹500' },
  { _id: 'f2', text: 'Secure UPI & Card payments' },
  { _id: 'f3', text: 'No returns currently · Exchanges for size/defect as applicable' },
];

/* ─────────────────────────────────────────────
   Icons
───────────────────────────────────────────── */
const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

/* ─────────────────────────────────────────────
   Single message segment
───────────────────────────────────────────── */
function Message({ text, highlight }) {
  return (
    <span className="ab-message">
      {text}
      {highlight && <span className="ab-message__highlight">{highlight}</span>}
    </span>
  );
}

/* ─────────────────────────────────────────────
   AnnouncementBar
───────────────────────────────────────────── */
function AnnouncementBar() {
  const [messages, setMessages] = useState([]);
  const [visible, setVisible]   = useState(false);
  const [closing, setClosing]   = useState(false);
  const barRef                  = useRef(null);
  const fetchRef                = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const controller = new AbortController();
      fetchRef.current = controller;
      const res = await fetch(`${API_URL}/announcements`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const json = await res.json();
      const list = Array.isArray(json?.data) ? json.data : [];
      setMessages(list.map((m) => ({
        id: m._id,
        _id: m._id,
        text: m.text || '',
        highlight: m.highlight || '',
      })));
    } catch (e) {
      if (e.name !== 'AbortError') setMessages([]);
    } finally {
      fetchRef.current = null;
    }
  }, []);

  useEffect(() => {
    fetchMessages();
    return () => fetchRef.current?.abort();
  }, [fetchMessages]);

  // Refetch on window focus for live admin updates (no delay)
  useEffect(() => {
    const onFocus = () => fetchMessages();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchMessages]);

  // Read storage on mount — avoids flash of bar then instant hide
  useEffect(() => {
    const dismissed = sessionStorage.getItem(STORAGE_KEY);
    if (dismissed !== 'true') setVisible(true);
  }, []);

  const handleClose = () => {
    setClosing(true);
    barRef.current?.addEventListener('transitionend', () => {
      setVisible(false);
      sessionStorage.setItem(STORAGE_KEY, 'true');
    }, { once: true });
  };

  // Fallback in case transitionend doesn't fire (reduced-motion / old browser)
  useEffect(() => {
    if (!closing) return;
    const t = setTimeout(() => {
      setVisible(false);
      sessionStorage.setItem(STORAGE_KEY, 'true');
    }, 350);
    return () => clearTimeout(t);
  }, [closing]);

  const list = messages.length > 0 ? messages : FALLBACK_MESSAGES;
  const MESSAGES = list.map((m) => ({
    id: m._id || m.id,
    text: m.text,
    highlight: m.highlight,
  }));

  if (!visible) return null;

  // Duplicate messages 3× for seamless infinite scroll
  const repeated = [...MESSAGES, ...MESSAGES, ...MESSAGES];

  return (
    <div
      ref={barRef}
      className={`ab${closing ? ' ab--closing' : ''}`}
      role="region"
      aria-label="Site announcements"
    >
      {/* SEO-visible static text — screen readers + crawlers */}
      <p className="ab__sr-text">
        {MESSAGES.map(m => `${m.text}${m.highlight || ''}`).join(' · ')}
      </p>

      {/* Scrolling track — aria-hidden so screen readers use the static text above */}
      <div className="ab__track" aria-hidden="true">
        <div className="ab__belt">
          {repeated.map((msg, i) => (
            <span key={`${msg.id}-${i}`} className="ab__segment">
              <Message text={msg.text} highlight={msg.highlight} />
              <span className="ab__sep" aria-hidden="true">·</span>
            </span>
          ))}
        </div>
      </div>

      {/* Close — visible on ALL screen sizes */}
      <button
        className="ab__close"
        onClick={handleClose}
        aria-label="Dismiss announcement bar"
        type="button"
      >
        <CloseIcon />
      </button>
    </div>
  );
}

export default AnnouncementBar;
